/* @jsx jsx */
import { css, jsx } from '@emotion/react';
import type { Interpolation } from '@emotion/react';

import * as React from 'react';
import Media from 'react-media';

import type UserAgent from 'js/lib/useragent';

import { Grid, useTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import Carousel from 'bundles/design-system/components/Carousel';
import type { CarouselSettings } from 'bundles/design-system/components/types';
import type { InstructorsFieldsFragment } from 'bundles/premium-certificate/__generated__/contentfulTypes';
import CertificateInstructor from 'bundles/premium-certificate/components/mastertrack/CertificateInstructor';
import Container from 'bundles/xddp/components/shared/Container';

import _t from 'i18n!nls/premium-certificate';

type Props = InstructorsFieldsFragment & {
  userAgent?: UserAgent;
};

const styles: Record<string, Interpolation<Theme>> = {
  instructorsWrapper: (theme: Theme) => css`
    padding: 4.5rem 0;

    @media (max-width: ${theme.breakpoints.values.md}px) {
      padding: 3rem 0;
    }
  `,
  carouselContainer: css`
    width: 100%;

    .rc-Carousel {
      > .slick-slider {
        width: auto;

        > .slick-list {
          padding: 0 0.75rem !important;
        }
      }
    }
  `,
  insturctorsTitle: (theme: Theme) => css`
    font-weight: 700;
    /* stylelint-disable-next-line font-family-no-missing-generic-family-keyword */
    font-family: OpenSans;
    font-size: 1.5rem;
    line-height: 1.875rem;
    text-align: center;
    margin-bottom: 3rem;
    padding: 0 0.75rem;

    @media (max-width: ${theme.breakpoints.values.md}px) {
      text-align: left;
      margin-bottom: 1.125rem;
    }
  `,
};

const carouselSettings: CarouselSettings = {
  dots: true,
  slidesPerRow: 3,
  slidesToScroll: 1,
  infinite: false,
  centerMode: true,
  arrows: false,
};

const CertificateInstructorsList: React.FunctionComponent<Props> = ({
  instructorTitle,
  instructorsCollection,
  userAgent,
}) => {
  const theme = useTheme();
  if (!instructorsCollection || !instructorsCollection.items) {
    return null;
  }

  const instructorsList = instructorsCollection?.items;
  return (
    <div id="instructors" data-test="CertificateInstructorsList" data-e2e="instructors" css={styles.instructorsWrapper}>
      <Container>
        <Grid container justifyContent="center">
          <Grid item>
            <h2 css={styles.insturctorsTitle}>{instructorTitle ?? _t('Instructors')}</h2>
          </Grid>
        </Grid>
        <Media query={{ minWidth: theme.breakpoints.values.md }} defaultMatches={userAgent?.isMobileBrowser}>
          {(matches) =>
            matches ? (
              <Grid container>
                {instructorsList.map((instructorProfile) => (
                  <CertificateInstructor
                    name={instructorProfile?.name}
                    position={instructorProfile?.position}
                    profilePicture={instructorProfile?.profilePicture}
                    key={instructorProfile?.name}
                  />
                ))}
              </Grid>
            ) : (
              <Grid container>
                <div css={styles.carouselContainer}>
                  <Carousel settings={carouselSettings} data-test="mobileCarousel">
                    {instructorsList.map((instructorProfile) => (
                      <CertificateInstructor
                        name={instructorProfile?.name}
                        position={instructorProfile?.position}
                        profilePicture={instructorProfile?.profilePicture}
                        key={instructorProfile?.name}
                      />
                    ))}
                  </Carousel>
                </div>
              </Grid>
            )
          }
        </Media>
      </Container>
    </div>
  );
};

export default CertificateInstructorsList;
